import type { Size } from "../../utils/controlStyles";
import { TextField } from "../TextField";

export interface SearchControlProps {
  /** Current search value */
  value: string;
  /** Callback when search value changes */
  onChange: (value: string) => void;
  /** Optional callback to clear search */
  onClear?: () => void;
  /** Placeholder text */
  placeholder?: string;
  /** Size variant */
  size?: Size;
  /** Additional CSS classes */
  className?: string;
  /** Shows a loading spinner instead of search icon */
  isLoading?: boolean;
}

/**
 * SearchControl
 *
 * A search input with magnifying glass icon and optional clear button.
 * Thin wrapper around TextField configured for search use cases.
 *
 * Example usage:
 * ```tsx
 * <SearchControl
 *   value={searchQuery}
 *   onChange={setSearchQuery}
 *   onClear={() => setSearchQuery('')}
 *   placeholder="Search sites..."
 * />
 * ```
 */
export function SearchControl({
  value,
  onChange,
  onClear,
  placeholder = "Search...",
  size = "sm",
  className = "",
  isLoading = false,
}: SearchControlProps) {
  return (
    <div className={`flex-1 min-w-0 sm:max-w-sm ${className}`}>
      <TextField
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        showSearchIcon={true}
        isClearable={!!value && !!onClear}
        onClear={onClear}
        size={size}
        aria-label={placeholder}
        reserveErrorSpace={false}
        isLoading={isLoading}
      />
    </div>
  );
}
